import { storeToRefs } from 'pinia';
import SafeLocalStorage from 'safe-localstorage';
import { route } from 'quasar/wrappers';
import {
  createRouter,
  createWebHashHistory,
  createWebHistory,
  NavigationGuardNext,
  RouteLocationNormalized,
} from 'vue-router';
import routes from './routes';
import { updateTitle } from 'services/title';
import { useAuthStore } from '#src/store/auth';

declare module 'vue-router' {
  interface RouteMeta {
    title: string;
    auth?: boolean;
    noNav?: boolean;
  }
}

export default route(() => {
  const createHistory = process.env.VUE_ROUTER_MODE === 'history' ? createWebHistory : createWebHashHistory;

  const Router = createRouter({
    scrollBehavior: () => ({ left: 0, top: 0 }),
    routes,
    // Leave this as is and make changes in quasar.conf.js instead!
    // quasar.conf.js -> build -> vueRouterMode
    // quasar.conf.js -> build -> publicPath
    history: createHistory(process.env.VUE_ROUTER_BASE),
  });

  Router.beforeEach(applyRouteTitle);

  return Router;
});

export function applyRouteTitle(to: RouteLocationNormalized, _: RouteLocationNormalized, next: NavigationGuardNext) {
  const nearestWithTitle = [...to.matched].reverse().find(({ meta }) => meta?.title);

  // If a route with a title was found, set the document (page) title to that value.
  if (nearestWithTitle) {
    updateTitle(nearestWithTitle.meta.title);
  }

  next();
}

export async function checkForAuth(to: RouteLocationNormalized, next: NavigationGuardNext) {
  const authStore = useAuthStore();
  const { profile } = storeToRefs(authStore);

  if (['/auth/login', '/error', '/forbidden', '/logged-out'].includes(to.path) || profile.value) {
    next();
    return;
  }

  // Load profile
  await authStore.getProfile();

  // Check for profile again
  if (profile.value) {
    next();
    return;
  }

  // Save current location to redirect back to it after auth
  SafeLocalStorage.set('loginRedirect', location.href);
  next(false);
  location.href = '/auth/login';
}
